import React, { useEffect, useState, useRef } from 'react';
import { getRequest } from '../services/apiService';
import html2canvas from 'html2canvas';
import { FileText, Image, Eye } from 'lucide-react';

const UpcomingMilestones = ({ selectedProject, selectedShip }) => {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const tableRef = useRef(null);

  // Fetch milestones when project or ship changes
  useEffect(() => {
    if (!selectedProject || !selectedShip) {
      setMilestones([]);
      return;
    }
    const fetchMilestones = async () => {
      setLoading(true);
      try {
        const res = await getRequest(`/transaction/upcoming-milestones/?ship_id=${selectedShip}`);
        setMilestones(res || []);
      } catch {
        setMilestones([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMilestones();
  }, [selectedProject, selectedShip]);

  // Find the next milestone (earliest due date)
  const nextMilestone = milestones.length
    ? milestones.reduce((a, b) => (a.latest_due_date < b.latest_due_date ? a : b))
    : null;

  // Download CSV
  const handleDownloadCSV = () => {
    if (!milestones.length) return;
    const headers = ['Milestone', 'Latest Due Date', 'Completion %'];
    const csvRows = [headers.join(',')];
    milestones.forEach(m => {
      csvRows.push([
        '"' + m.milestone_name + '"',
        m.latest_due_date,
        m.completion_percent
      ].join(','));
    });
    const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'upcoming-milestones.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download PNG
  const handleDownloadPNG = () => {
    if (!tableRef.current) return;
    html2canvas(tableRef.current).then(canvas => {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = 'upcoming-milestones.png';
      link.click();
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mb-6">
      {/* Header row: title left, actions right */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
        <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-0">Upcoming Milestones</h2>
        <div className="flex gap-2 items-center mt-2 md:mt-0">
          {/* Action buttons */}
          <button
            className="flex items-center gap-1 px-3 py-1 rounded bg-white hover:bg-gray-100 text-indigo-600 text-sm border border-indigo-100 font-medium"
            onClick={() => setShowDetails(true)}
            disabled={!milestones.length}
            title="View Details"
          >
            <Eye size={16} /> View Details
          </button>
          <button
            className="flex items-center gap-1 px-3 py-1 rounded bg-gray-100 hover:bg-gray-300 text-gray-700 text-sm border border-gray-200"
            onClick={handleDownloadCSV}
            disabled={!milestones.length}
            title="Download CSV"
          >
            <FileText size={16} /> CSV
          </button>
          <button
            className="flex items-center gap-1 px-3 py-1 rounded bg-gray-100 hover:bg-gray-300 text-gray-700 text-sm border border-gray-200"
            onClick={handleDownloadPNG}
            disabled={!milestones.length}
            title="Download PNG"
          >
            <Image size={16} /> PNG
          </button>
        </div>
      </div>
      {/* Prompt if not selected */}
      {(!selectedProject || !selectedShip) ? (
        <div className="text-gray-500 text-center py-8">
          Please select a project and ship to view upcoming milestones.
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center h-32 text-gray-500">
          Loading milestones...
        </div>
      ) : (
        <div className="overflow-x-auto" ref={tableRef}>
          <table className="min-w-full divide-y divide-gray-200 border">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Milestone</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Latest Due Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Completion %</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {milestones.map((m, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-2">
                    <div className="font-semibold text-gray-800">{m.milestone_name}</div>
                    {/* <div className="text-xs text-gray-500">{m.milestone_id && `ID: ${m.milestone_id}`}</div> */}
                  </td>
                  <td className="px-4 py-2">{m.latest_due_date}</td>
                  <td className="px-4 py-2">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                      {m.completion_percent}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Details Modal */}
      {showDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-2xl max-h-[90vh] overflow-auto flex flex-col">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-indigo-50">
              <h3 className="text-lg font-semibold text-indigo-900">Upcoming Milestones - Details</h3>
              <button 
                onClick={() => setShowDetails(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-6 overflow-auto flex-grow">
              <table className="min-w-full divide-y divide-gray-200 border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Milestone</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Latest Due Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Completion %</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {milestones.map((m, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2">
                        <div className="font-semibold text-gray-800">{m.milestone_name}</div>
                        <div className="text-xs text-gray-500">{m.milestone_id && `ID: ${m.milestone_id}`}</div>
                      </td>
                      <td className="px-4 py-2">{m.latest_due_date}</td>
                      <td className="px-4 py-2">
                        <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                          {m.completion_percent}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcomingMilestones; 